import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Gift, Search, X, ListChecks, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cx } from '@/lib/format';
import { slugify } from '@/data/nav';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';
const labelCls = 'text-xs font-medium text-muted-foreground';
const chip = (on) => cx('rounded-full border px-3 py-1.5 text-xs font-medium transition',
  on ? 'border-[hsl(var(--primary))] bg-[hsl(var(--gaze-ivory))] text-[hsl(var(--primary))]' : 'border-border text-muted-foreground hover:border-[hsl(var(--gaze-gold))]');

const EMPTY_Q = { key: '', title: '', subtitle: '', qtype: 'choice', active: true, sort_order: 0, options: [] };
const EMPTY_RULE = { name: '', active: true, priority: 0, conditions: {}, categories: [], tags: [], product_ids: [] };

const StatusPill = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={cx('rounded-full px-2.5 py-1 text-[11px] font-semibold', on ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500')}>
    {on ? 'Active' : 'Inactive'}
  </button>
);

export default function AdminGiftFinder() {
  const [tab, setTab] = useState('questions');
  const [questions, setQuestions] = useState([]);
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({ categories: [], tags: [] });
  const [loading, setLoading] = useState(true);

  // Question editor
  const [qOpen, setQOpen] = useState(false);
  const [qEditing, setQEditing] = useState(null);
  const [qForm, setQForm] = useState(EMPTY_Q);
  const [qKeyTouched, setQKeyTouched] = useState(false);

  // Rule editor
  const [rOpen, setROpen] = useState(false);
  const [rEditing, setREditing] = useState(null);
  const [rForm, setRForm] = useState(EMPTY_RULE);
  const [tagText, setTagText] = useState('');
  const [prodQ, setProdQ] = useState('');
  const [prodResults, setProdResults] = useState([]);
  const [selProds, setSelProds] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/gift-finder/questions'),
      api.get('/admin/gift-finder/rules'),
      api.get('/admin/gift-finder/meta'),
    ]).then(([q, r, m]) => { setQuestions(q.data || []); setRules(r.data || []); setMeta(m.data || { categories: [], tags: [] }); })
      .catch(() => toast.error('Could not load Gift Finder data'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!rOpen || !prodQ.trim()) { setProdResults([]); return; }
    const t = setTimeout(() => {
      api.get('/admin/products', { params: { q: prodQ, limit: 8 } }).then((r) => setProdResults(r.data.items || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [prodQ, rOpen]);

  const catName = useMemo(() => Object.fromEntries(meta.categories.map((c) => [c.slug, c.name])), [meta.categories]);

  /* ---------- Questions ---------- */
  const openNewQ = () => { setQEditing(null); setQForm({ ...EMPTY_Q, options: [] }); setQKeyTouched(false); setQOpen(true); };
  const openEditQ = (q) => { setQEditing(q); setQForm({ ...EMPTY_Q, ...q, options: (q.options || []).map((o) => ({ ...o })) }); setQKeyTouched(true); setQOpen(true); };

  const setOpt = (i, patch) => setQForm((f) => ({ ...f, options: f.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) }));
  const moveOpt = (i, dir) => setQForm((f) => {
    const arr = [...f.options]; const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, options: arr };
  });

  const saveQuestion = async () => {
    if (!qForm.title.trim()) return toast.error('Question title is required');
    const opts = qForm.options.filter((o) => (o.label || '').trim());
    if (opts.length < 2) return toast.error('Add at least 2 answer options');
    const payload = { ...qForm, key: slugify(qForm.key || qForm.title), options: opts.map((o, i) => ({ ...o, value: slugify(o.value || o.label), sort_order: i, budget_min: o.budget_min === '' ? null : o.budget_min, budget_max: o.budget_max === '' ? null : o.budget_max })) };
    setSaving(true);
    try {
      if (qEditing) await api.put(`/admin/gift-finder/questions/${qEditing.id}`, payload);
      else await api.post('/admin/gift-finder/questions', payload);
      toast.success(qEditing ? 'Question updated' : 'Question created');
      setQOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const toggleQActive = async (q) => {
    try {
      await api.put(`/admin/gift-finder/questions/${q.id}`, { ...q, active: !q.active });
      setQuestions((qs) => qs.map((x) => (x.id === q.id ? { ...x, active: !q.active } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const deleteQ = async (q) => {
    if (!window.confirm(`Delete question "${q.title}"? Rules referencing it will drop that condition.`)) return;
    try { await api.delete(`/admin/gift-finder/questions/${q.id}`); toast.success('Question deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const moveQ = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= questions.length) return;
    const arr = [...questions];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setQuestions(arr);
    try { await api.post('/admin/gift-finder/questions/reorder', { ordered_ids: arr.map((x) => x.id) }); }
    catch { toast.error('Reorder failed'); load(); }
  };

  /* ---------- Rules ---------- */
  const openNewRule = () => { setREditing(null); setRForm({ ...EMPTY_RULE, conditions: {}, categories: [], tags: [], product_ids: [] }); setTagText(''); setSelProds([]); setProdQ(''); setROpen(true); };
  const openEditRule = (r) => {
    setREditing(r);
    setRForm({ ...EMPTY_RULE, ...r, conditions: { ...(r.conditions || {}) } });
    setTagText((r.tags || []).join(', '));
    setSelProds(r.products || []);
    setProdQ('');
    setROpen(true);
  };

  const toggleCond = (key, value) => setRForm((f) => {
    const cur = f.conditions[key] || [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    const conditions = { ...f.conditions, [key]: next };
    if (!next.length) delete conditions[key];
    return { ...f, conditions };
  });

  const toggleCat = (slug) => setRForm((f) => ({ ...f, categories: f.categories.includes(slug) ? f.categories.filter((s) => s !== slug) : [...f.categories, slug] }));

  const saveRule = async () => {
    if (!rForm.name.trim()) return toast.error('Rule name is required');
    const payload = {
      ...rForm,
      priority: Number(rForm.priority || 0),
      tags: tagText.split(',').map((t) => t.trim()).filter(Boolean),
      product_ids: selProds.map((p) => p.id),
    };
    setSaving(true);
    try {
      if (rEditing) await api.put(`/admin/gift-finder/rules/${rEditing.id}`, payload);
      else await api.post('/admin/gift-finder/rules', payload);
      toast.success(rEditing ? 'Rule updated' : 'Rule created');
      setROpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const toggleRuleActive = async (r) => {
    try {
      await api.put(`/admin/gift-finder/rules/${r.id}`, { ...r, active: !r.active });
      setRules((rs) => rs.map((x) => (x.id === r.id ? { ...x, active: !r.active } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const deleteRule = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try { await api.delete(`/admin/gift-finder/rules/${r.id}`); toast.success('Rule deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const qByKey = useMemo(() => Object.fromEntries(questions.map((q) => [q.key, q])), [questions]);
  const condSummary = (r) => Object.entries(r.conditions || {}).map(([k, vals]) => {
    const q = qByKey[k];
    const labels = vals.map((v) => (q?.options || []).find((o) => o.value === v)?.label || v);
    return `${q?.title?.split('?')[0] || k}: ${labels.join(', ')}`;
  });

  return (
    <div data-testid="admin-gift-finder">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold"><Gift size={22} className="gaze-gold" /> Gift Finder</h1>
          <p className="text-sm text-muted-foreground">Manage quiz questions, answers and product matching rules for the storefront Gift Finder.</p>
        </div>
        {tab === 'questions'
          ? <button onClick={openNewQ} data-testid="gift-add-question-btn" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-5 py-2.5 text-sm font-semibold text-white"><Plus size={15} /> Add Question</button>
          : <button onClick={openNewRule} data-testid="gift-add-rule-btn" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-5 py-2.5 text-sm font-semibold text-white"><Plus size={15} /> Add Rule</button>}
      </div>

      <div className="mt-4 flex gap-2">
        <button onClick={() => setTab('questions')} data-testid="gift-tab-questions" className={chip(tab === 'questions')}><ListChecks size={12} className="mr-1 inline" /> Questions ({questions.length})</button>
        <button onClick={() => setTab('rules')} data-testid="gift-tab-rules" className={chip(tab === 'rules')}><Wand2 size={12} className="mr-1 inline" /> Matching Rules ({rules.length})</button>
      </div>

      {loading ? <p className="mt-8 text-sm text-muted-foreground">Loading…</p> : tab === 'questions' ? (
        <div className="mt-4 space-y-2">
          {questions.map((q, idx) => (
            <div key={q.id} data-testid={`gift-question-row-${q.key}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-white p-4">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveQ(idx, -1)} disabled={idx === 0} data-testid={`gift-question-up-${q.key}`} className="rounded p-0.5 text-muted-foreground hover:bg-[hsl(var(--gaze-ivory))] disabled:opacity-30"><ArrowUp size={13} /></button>
                <button onClick={() => moveQ(idx, 1)} disabled={idx === questions.length - 1} className="rounded p-0.5 text-muted-foreground hover:bg-[hsl(var(--gaze-ivory))] disabled:opacity-30"><ArrowDown size={13} /></button>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{q.title}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  <span className="rounded bg-[hsl(var(--gaze-ivory))] px-1.5 py-0.5 font-mono">{q.key}</span>
                  <span className="ml-2 uppercase tracking-wide">{q.qtype}</span>
                  <span className="ml-2">{(q.options || []).length} options · {(q.options || []).filter((o) => o.active !== false).length} active</span>
                </p>
              </div>
              <StatusPill on={q.active !== false} onClick={() => toggleQActive(q)} testid={`gift-question-toggle-${q.key}`} />
              <button onClick={() => openEditQ(q)} data-testid={`gift-question-edit-${q.key}`} className="rounded-lg border border-border p-2 hover:bg-[hsl(var(--gaze-ivory))]"><Pencil size={14} /></button>
              <button onClick={() => deleteQ(q)} data-testid={`gift-question-delete-${q.key}`} className="rounded-lg border border-border p-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
            </div>
          ))}
          {!questions.length && <p className="mt-8 text-sm text-muted-foreground">No questions yet. Add your first question.</p>}
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {rules.map((r) => (
            <div key={r.id} data-testid="gift-rule-row" className="flex flex-wrap items-start gap-3 rounded-2xl border border-border bg-white p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{r.name} <span className="ml-1 rounded bg-[hsl(var(--gaze-ivory))] px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">priority {r.priority || 0}</span></p>
                <div className="mt-1 space-y-0.5">
                  {condSummary(r).map((s, i) => <p key={i} className="text-[11px] text-muted-foreground">If {s}</p>)}
                </div>
                <p className="mt-1 text-[11px] font-medium text-[hsl(var(--primary))]">
                  → {(r.categories || []).map((s) => catName[s] || s).join(', ')}
                  {(r.tags || []).length ? ` · tags: ${r.tags.join(', ')}` : ''}
                  {(r.products || []).length ? ` · ${r.products.length} hand-picked product${r.products.length > 1 ? 's' : ''}` : ''}
                </p>
              </div>
              <StatusPill on={r.active !== false} onClick={() => toggleRuleActive(r)} testid="gift-rule-toggle" />
              <button onClick={() => openEditRule(r)} data-testid="gift-rule-edit" className="rounded-lg border border-border p-2 hover:bg-[hsl(var(--gaze-ivory))]"><Pencil size={14} /></button>
              <button onClick={() => deleteRule(r)} data-testid="gift-rule-delete" className="rounded-lg border border-border p-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
            </div>
          ))}
          {!rules.length && <p className="mt-8 text-sm text-muted-foreground">No rules yet. Rules map customer answers to products.</p>}
        </div>
      )}

      {/* ---------- Question editor ---------- */}
      <Dialog open={qOpen} onOpenChange={setQOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{qEditing ? `Edit · ${qEditing.title}` : 'New Question'}</DialogTitle>
            <DialogDescription className="sr-only">Gift finder question editor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Question title *</label>
                <input value={qForm.title} onChange={(e) => { const v = e.target.value; setQForm((f) => ({ ...f, title: v, key: qKeyTouched ? f.key : slugify(v) })); }} className={field} data-testid="gift-question-title-input" />
              </div>
              <div>
                <label className={labelCls}>Key (used in rules)</label>
                <input value={qForm.key} onChange={(e) => { setQKeyTouched(true); setQForm((f) => ({ ...f, key: e.target.value })); }} className={cx(field, 'font-mono')} data-testid="gift-question-key-input" />
              </div>
              <div>
                <label className={labelCls}>Type</label>
                <select value={qForm.qtype} onChange={(e) => setQForm((f) => ({ ...f, qtype: e.target.value }))} className={field} data-testid="gift-question-type-select">
                  <option value="choice">Choice (recipient, occasion, style…)</option>
                  <option value="budget">Budget (price range)</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Subtitle (optional)</label>
                <input value={qForm.subtitle || ''} onChange={(e) => setQForm((f) => ({ ...f, subtitle: e.target.value }))} className={field} />
              </div>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={qForm.active} onChange={(e) => setQForm((f) => ({ ...f, active: e.target.checked }))} data-testid="gift-question-active-checkbox" /> Active on storefront</label>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold">Answer options</p>
                <button onClick={() => setQForm((f) => ({ ...f, options: [...f.options, { label: '', value: '', active: true, budget_min: null, budget_max: null }] }))} data-testid="gift-add-option-btn" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold"><Plus size={12} /> Add option</button>
              </div>
              <div className="space-y-2">
                {qForm.options.map((o, i) => (
                  <div key={o.id || i} className="flex flex-wrap items-center gap-2 rounded-xl border border-border p-2">
                    <div className="flex flex-col">
                      <button onClick={() => moveOpt(i, -1)} disabled={i === 0} className="rounded p-0.5 text-muted-foreground disabled:opacity-30"><ArrowUp size={11} /></button>
                      <button onClick={() => moveOpt(i, 1)} disabled={i === qForm.options.length - 1} className="rounded p-0.5 text-muted-foreground disabled:opacity-30"><ArrowDown size={11} /></button>
                    </div>
                    <input value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })} placeholder="Label (e.g. Partner)" className={cx(field, 'flex-1')} data-testid={`gift-option-label-${i}`} />
                    {qForm.qtype === 'budget' && (
                      <>
                        <input type="number" value={o.budget_min ?? ''} onChange={(e) => setOpt(i, { budget_min: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Min ₹" className={cx(field, 'w-24')} data-testid={`gift-option-min-${i}`} />
                        <input type="number" value={o.budget_max ?? ''} onChange={(e) => setOpt(i, { budget_max: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Max ₹" className={cx(field, 'w-24')} data-testid={`gift-option-max-${i}`} />
                      </>
                    )}
                    <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={o.active !== false} onChange={(e) => setOpt(i, { active: e.target.checked })} /> On</label>
                    <button onClick={() => setQForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }))} className="rounded p-1.5 text-red-600 hover:bg-red-50" data-testid={`gift-option-remove-${i}`}><X size={13} /></button>
                  </div>
                ))}
                {!qForm.options.length && <p className="text-xs text-muted-foreground">No options yet — add at least 2.</p>}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <button onClick={() => setQOpen(false)} className="rounded-full border border-border px-5 py-2 text-sm font-semibold">Cancel</button>
              <button onClick={saveQuestion} disabled={saving} data-testid="gift-question-save-btn" className="rounded-full bg-[hsl(var(--primary))] px-6 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save Question'}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- Rule editor ---------- */}
      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{rEditing ? `Edit Rule · ${rEditing.name}` : 'New Matching Rule'}</DialogTitle>
            <DialogDescription className="sr-only">Gift finder rule editor</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Rule name *</label>
                <input value={rForm.name} onChange={(e) => setRForm((f) => ({ ...f, name: e.target.value }))} className={field} data-testid="gift-rule-name-input" />
              </div>
              <div>
                <label className={labelCls}>Priority (higher wins ties)</label>
                <input type="number" value={rForm.priority} onChange={(e) => setRForm((f) => ({ ...f, priority: e.target.value }))} className={field} data-testid="gift-rule-priority-input" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm sm:col-span-3"><input type="checkbox" checked={rForm.active} onChange={(e) => setRForm((f) => ({ ...f, active: e.target.checked }))} data-testid="gift-rule-active-checkbox" /> Rule active</label>
            </div>

            <div>
              <p className="text-sm font-semibold">When the customer answers…</p>
              <p className="mb-2 text-[11px] text-muted-foreground">Pick answer values. Multiple values within a question = any of them; an answer outside the selection disqualifies the rule.</p>
              <div className="space-y-3">
                {questions.map((qq) => (
                  <div key={qq.id}>
                    <p className="mb-1 text-xs font-medium">{qq.title} <span className="font-mono text-[10px] text-muted-foreground">({qq.key})</span></p>
                    <div className="flex flex-wrap gap-1.5">
                      {(qq.options || []).map((o) => (
                        <button key={o.value} onClick={() => toggleCond(qq.key, o.value)} data-testid={`gift-cond-${qq.key}-${o.value}`} className={chip((rForm.conditions[qq.key] || []).includes(o.value))}>{o.label}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold">…show products matching</p>
              <label className={labelCls}>Categories</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {meta.categories.map((c) => (
                  <button key={c.slug} onClick={() => toggleCat(c.slug)} data-testid={`gift-rule-cat-${c.slug}`} className={chip(rForm.categories.includes(c.slug))}>{c.name}</button>
                ))}
              </div>
              <div className="mt-3">
                <label className={labelCls}>Tags (comma separated)</label>
                <input value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="e.g. gift, festive, premium" className={field} data-testid="gift-rule-tags-input" />
                {!!meta.tags.length && <p className="mt-1 text-[11px] text-muted-foreground">Existing tags: {meta.tags.join(', ')}</p>}
              </div>
              <div className="mt-3">
                <label className={labelCls}>Hand-picked products</label>
                <div className="relative mt-1">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input value={prodQ} onChange={(e) => setProdQ(e.target.value)} placeholder="Search products by name…" className={cx(field, 'pl-8')} data-testid="gift-rule-product-search" />
                </div>
                {!!prodResults.length && (
                  <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-xl border border-border p-1">
                    {prodResults.filter((p) => !selProds.some((s) => s.id === p.id)).map((p) => (
                      <button key={p.id} onClick={() => { setSelProds((s) => [...s, { id: p.id, name: p.name, price: p.price }]); setProdQ(''); }} data-testid="gift-rule-product-result" className="flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-xs hover:bg-[hsl(var(--gaze-ivory))]">
                        <span className="truncate">{p.name}</span><span className="ml-2 shrink-0 font-semibold">₹{p.price}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!!selProds.length && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selProds.map((p) => (
                      <span key={p.id} data-testid="gift-rule-selected-product" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gaze-ivory))] px-3 py-1 text-xs font-medium">
                        {p.name}
                        <button onClick={() => setSelProds((s) => s.filter((x) => x.id !== p.id))} className="text-red-600"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <button onClick={() => setROpen(false)} className="rounded-full border border-border px-5 py-2 text-sm font-semibold">Cancel</button>
              <button onClick={saveRule} disabled={saving} data-testid="gift-rule-save-btn" className="rounded-full bg-[hsl(var(--primary))] px-6 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save Rule'}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
